import React, { useState, useEffect, useCallback } from 'react';
import { loadOrCreateIdentity } from '../lib/identity';
import { getListingsForUser, saveIncomingListing, deleteListing, addListingListener } from '../lib/listings';
import { publishEvent } from '../lib/sync';
import { sanitizeUrl } from '../lib/sanitize';
import { ReportButton } from './ReportButton';
import { COLORS, SPACING } from '../styles/theme';

interface ShopTabProps {
  userId: string;
  isSelf: boolean;
}

export function ShopTab({ userId, isSelf }: ShopTabProps) {
  const [listings, setListings] = useState(() => getListingsForUser(userId));
  const [composing, setComposing] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', price: '', paymentUrl: '', category: '' });
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => setListings(getListingsForUser(userId)), [userId]);

  useEffect(() => {
    reload();
    return addListingListener(reload);
  }, [userId, reload]);

  const handleSave = async () => {
    if (!form.title.trim() || !form.price.trim() || !sanitizeUrl(form.paymentUrl)) return;
    setSaving(true);
    const identity = loadOrCreateIdentity();
    const event = await publishEvent('listing', {
      userId: identity.userId,
      username: identity.userId,
      title: form.title.trim(),
      description: form.description.trim(),
      price: form.price.trim(),
      paymentUrl: form.paymentUrl.trim(),
      category: form.category,
    });
    saveIncomingListing({
      id: event.id,
      userId: identity.userId,
      username: identity.userId,
      title: form.title.trim(),
      description: form.description.trim(),
      price: form.price.trim(),
      paymentUrl: form.paymentUrl.trim(),
      category: form.category,
      createdAt: event.timestamp,
    });
    setForm({ title: '', description: '', price: '', paymentUrl: '', category: '' });
    setComposing(false);
    setSaving(false);
  };

  const handleDelete = async (listingId: string) => {
    const identity = loadOrCreateIdentity();
    deleteListing(listingId, identity.userId);
    await publishEvent('delete_listing', { listingId, userId: identity.userId });
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: SPACING.sm,
    backgroundColor: '#fff',
    color: '#000',
    border: `2px inset ${COLORS.highlight}`,
    fontFamily: '"IBM Plex Mono", monospace',
    fontSize: '10px',
    boxSizing: 'border-box',
  };

  if (listings.length === 0 && !isSelf) return null;

  return (
    <div style={{ marginTop: SPACING.lg }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: SPACING.md, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: SPACING.sm }}>
        <span style={{ fontSize: '12px', fontWeight: 'bold', color: COLORS.text }}>Shop</span>
        {isSelf && !composing && (
          <button
            onClick={() => setComposing(true)}
            style={{ background: COLORS.accent, color: '#fff', border: 'none', padding: `${SPACING.xs} ${SPACING.sm}`, cursor: 'pointer', fontSize: '11px', fontFamily: 'monospace' }}
          >
            + Add item
          </button>
        )}
      </div>

      {isSelf && composing && (
        <div style={{ border: `2px outset ${COLORS.highlight}`, backgroundColor: COLORS.panel, padding: SPACING.md, marginBottom: SPACING.lg, display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
          <input style={inputStyle} placeholder="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} maxLength={80} />
          <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} placeholder="Description (optional)" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} maxLength={400} />
          <input style={inputStyle} placeholder='Price (e.g. "$10", "0.001 BTC", "free")' value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} maxLength={30} />
          <input style={inputStyle} placeholder="Payment / checkout URL (must start with https://)" value={form.paymentUrl} onChange={e => setForm(f => ({ ...f, paymentUrl: e.target.value }))} maxLength={500} />
          <input style={inputStyle} placeholder="Item type (e.g. music, hoodie, print, ticket)" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} maxLength={40} />
          <div style={{ display: 'flex', gap: SPACING.sm }}>
            <button onClick={handleSave} disabled={saving || !form.title.trim() || !form.price.trim() || !sanitizeUrl(form.paymentUrl)}
              style={{ flex: 1, background: COLORS.accent, color: '#fff', border: 'none', padding: SPACING.sm, cursor: 'pointer', fontFamily: 'monospace', fontSize: '11px' }}>
              {saving ? '...' : 'Post'}
            </button>
            <button onClick={() => setComposing(false)}
              style={{ background: 'transparent', border: `1px solid ${COLORS.border}`, color: COLORS.textMuted, padding: SPACING.sm, cursor: 'pointer', fontFamily: 'monospace', fontSize: '11px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {listings.length === 0 && isSelf && !composing && (
        <p style={{ fontSize: '12px', color: COLORS.textMuted }}>No items yet. Add something to sell.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
        {listings.map(listing => (
          <div key={listing.id} style={{ border: `1px solid ${COLORS.border}`, padding: SPACING.md, backgroundColor: COLORS.panel }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: SPACING.xs }}>
              <div>
                <span style={{ fontSize: '13px', fontWeight: 'bold', color: COLORS.text }}>{listing.title}</span>
                <span style={{ marginLeft: SPACING.sm, fontSize: '10px', color: COLORS.textMuted, textTransform: 'uppercase' }}>{listing.category}</span>
              </div>
              <span style={{ fontSize: '13px', fontWeight: 'bold', color: COLORS.accent, flexShrink: 0, marginLeft: SPACING.sm }}>{listing.price}</span>
            </div>
            {listing.description && (
              <p style={{ margin: `${SPACING.xs} 0`, fontSize: '11px', color: COLORS.textMuted, lineHeight: 1.5 }}>{listing.description}</p>
            )}
            <div style={{ display: 'flex', gap: SPACING.sm, marginTop: SPACING.sm, alignItems: 'center' }}>
              {sanitizeUrl(listing.paymentUrl) ? (
                <a href={sanitizeUrl(listing.paymentUrl)!} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: '11px', color: COLORS.accent, textDecoration: 'underline', fontFamily: 'monospace' }}>
                  Buy →
                </a>
              ) : (
                <span style={{ fontSize: '11px', color: COLORS.textMuted, fontFamily: 'monospace' }}>
                  [invalid url]
                </span>
              )}
              {!isSelf && <ReportButton targetId={listing.id} targetType="listing" />}
              {isSelf && (
                <button onClick={() => handleDelete(listing.id)}
                  style={{ background: 'transparent', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: '10px', textDecoration: 'underline', fontFamily: 'monospace' }}>
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
